import { ReactNode } from 'react';

import { HTTP_INTERNAL_SERVER_ERROR_STATUS } from '@vk-hr-tek/core/http';

import Can from '@app/ability/Can';
import { AppAbilities } from '@app/ability/ability';
import { useSelector } from '@app/hooks';
import { WidgetReadySignal } from '@app/context';

import { useAuth } from '../../auth';
import { selectStatus, selectError } from '../../user';
import { selectCandidateUserStatus } from '../../user/slice';
import { PageError, PageFallback } from '../page';
import { useApplication } from '../../auth/hooks/useApplication';
import { AbilityProvider } from '../AbilityProvider';

interface RequireAuthProps {
  children: ReactNode;
  resource: AppAbilities[1];
  role?: 'employee' | 'company' | 'general';
}

export const RequireAuth = ({ children, resource, role }: RequireAuthProps) => {
  const isAuthenticated = useAuth();
  const appType = useApplication();
  const status = useSelector(
    appType === 'candidateApp' ? selectCandidateUserStatus : selectStatus,
  );
  const error = useSelector(selectError);

  if (!isAuthenticated) {
    return <WidgetReadySignal />;
  }

  if (status === 'idle' || status === 'loading') {
    return <PageFallback />;
  }

  if (status === 'failed') {
    return (
      <WidgetReadySignal>
        <PageError
          status={error?.status || HTTP_INTERNAL_SERVER_ERROR_STATUS}
          traceId={error?.traceId}
          errorMessage="Произошла ошибка получения данных пользователя"
        />
      </WidgetReadySignal>
    );
  }

  if (!role) {
    return (
      <>
        <Can do="read" on={resource}>
          {children}
        </Can>
        <Can not do="read" on={resource}>
          <WidgetReadySignal>
            <PageError status={403} />
          </WidgetReadySignal>
        </Can>
      </>
    );
  } else {
    return (
      <AbilityProvider role={role}>
        <Can do="read" on={resource}>
          {children}
        </Can>
        <Can not do="read" on={resource}>
          <WidgetReadySignal>
            <PageError status={403} />
          </WidgetReadySignal>
        </Can>
      </AbilityProvider>
    );
  }
};
